import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Button, HStack, VStack, useToast } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { TableInvite, subscribeToPendingInvites, acceptInvite, declineInvite } from '../services/inviteService';
import { tableService } from '../services/tableService';
import walletService from '../services/walletService';
import { gameApiService } from '../services/gameApiService';
import { POKER_COLORS } from '../theme/designSystem';

interface InviteNotificationProps {
  currentUserId: string;
}

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const InviteNotification: React.FC<InviteNotificationProps> = ({ currentUserId }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [invites, setInvites] = useState<TableInvite[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUserId) return;
    const unsub = subscribeToPendingInvites(currentUserId, setInvites);
    return () => unsub();
  }, [currentUserId]);

  const handleAccept = async (invite: TableInvite) => {
    setProcessingId(invite.id);
    try {
      // Group game invite: register for the game (scheduled) or head to the
      // group's live table; no table checks needed here.
      if (invite.kind === 'group_event') {
        await acceptInvite(invite.id);
        if (invite.eventId && !invite.tableId) await gameApiService.registerGroupEvent(invite.eventId, true).catch(() => {});
        navigate(invite.url || (invite.groupId ? `/groups/${invite.groupId}` : '/groups'));
        return;
      }
      // Group invite: the server joins them (private groups too) and marks
      // the invite accepted in the same transaction.
      if (invite.kind === 'group' && invite.groupId) {
        const r = await gameApiService.joinGroup({ groupId: invite.groupId, inviteId: invite.id });
        if (!r.success) throw new Error(r.error || 'Could not join the group');
        navigate(`/groups/${invite.groupId}`);
        return;
      }

      //  Get table info to check currency and min buy-in

      const table = await tableService.getTable(invite.tableId);
      if (!table) {
        throw new Error('Table not found');
      }

      //  Check if PSS Coins table - verify user has enough balance

      if (table.currency === 'pkcash') {
        const wallet = await walletService.getWallet(currentUserId);
        const pkCashBalance = wallet?.pkCash || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (pkCashBalance < minBuyIn) {
          toast({
            title: 'Insufficient PSS Coins',
            description: `You need at least $${minBuyIn} PSS Coins to join this table. You have $${pkCashBalance.toFixed(2)}.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          // Decline the invite since they can't join
          await declineInvite(invite.id);
          setProcessingId(null);
          return;
        }
      }

      //  Check PlayCoins balance for PlayCoin tables too

      if (table.currency === 'playcoins') {
        const wallet = await walletService.getWallet(currentUserId);
        const playCoinsBalance = wallet?.playCoins || 0;
        const minBuyIn = table.minBuyIn || 40;

        if (playCoinsBalance < minBuyIn) {
          toast({
            title: 'Insufficient PlayCoins',
            description: `You need at least ${minBuyIn.toLocaleString()} PlayCoins to join this table. You have ${playCoinsBalance.toLocaleString()}.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          await declineInvite(invite.id);
          setProcessingId(null);
          return;
        }
      }

      const tableId = await acceptInvite(invite.id);
      navigate(`/game/${tableId}`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to join table',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (invite: TableInvite) => {
    setProcessingId(invite.id);
    try {
      await declineInvite(invite.id);
    } catch (error) {
      console.error('Error declining invite:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (invites.length === 0) return null;

  return (
    <Box
      position="fixed"
      top="80px"
      right="20px"
      zIndex={3000}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      {invites.map((invite) => (
        <Box
          key={invite.id}
          bg={POKER_COLORS.darkBg}
          border={`1px solid ${POKER_COLORS.cyan}66`}
          borderRadius="12px"
          p={4}
          minW="300px"
          maxW="400px"
          boxShadow={`0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${POKER_COLORS.cyan}22`}
          animation={`${slideIn} 0.3s ease`}
        >
          <HStack spacing={3} align="flex-start">
            <Text fontSize="28px"></Text>

            <VStack align="flex-start" spacing={1} flex={1}>
              <Text color={POKER_COLORS.cyan} fontWeight="600" fontSize="12px" textTransform="uppercase" letterSpacing="0.5px">
                {invite.kind === 'group' ? 'Group Invite' : invite.kind === 'group_event' ? 'Group Game Invite' : 'Table Invite'}
              </Text>
              <Text color="gray.200" fontSize="14px">
                <Text as="span" fontWeight="bold" color="white">{invite.fromUserName}</Text>
                {' invited you to '}
                <Text as="span" fontWeight="bold" color="white">{invite.tableName}</Text>
              </Text>
            </VStack>
          </HStack>
          
          <HStack mt={3} spacing={2} justify="flex-end">
            <Button
              size="sm"
              bg={POKER_COLORS.cyan}
              color={POKER_COLORS.darkTeal}
              fontWeight="bold"
              _hover={{ transform: 'scale(1.05)' }}
              onClick={() => handleAccept(invite)}
              isLoading={processingId === invite.id}
              loadingText="Joining..."
            >
              {invite.kind === 'group_event' && !invite.tableId ? 'Register' : 'Join'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'red.400', bg: 'red.900' }}
              onClick={() => handleDecline(invite)}
              isDisabled={processingId === invite.id}
            >
              Decline
            </Button>
          </HStack>
        </Box>
      ))}
    </Box>
  );
};

export default InviteNotification;